import { articleDetailQuery } from "../../../features/article/hooks/useArticleData";
import { useTranslation } from "react-i18next";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import BottomPanel from "../../app/panels/BottomPanel";
import { cn } from "../../../lib/utils";
import { InfoPopover } from "./InfoPopover";
import { SourceMethodReadout } from "./SourceMethodReadout";
import { citationIdentity } from "../../../domains/gpt/citation";
import { publicationYear } from "../../../domains/gpt/chatSource";
import { SourceLink, type SourceTarget } from "../../ui/SourceLink";
import { ChatSourceIdentity, type ChatSource } from "../../ui/ChatSources";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../../domains/app/utils/getArticleTypeText";
import { SignalMetric, SourceQualityExplanation } from "../../ui/SourceSignals";
import { useApiQuery } from "../../../services/api";
import { fieldBand, indexedIn } from "../../../domains/journal/journalRank";
import {
  JournalStandingSection,
  journalStatesAnything,
  StudyAppraisalLedger,
  type ReadingLanguage,
  type JournalStanding,
  type ShownAppraisal,
} from "../../works-score/WorksScoreScreen";
import { type EvidenceSourceView } from "../../../domains/gpt/hooks/useEvidenceGptChat";
import { CitedPassages } from "../../ui/CitedPassages";

function samehostPath(url: string): string | null {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

// A count metric (not a [0,1] signal): the number in a chip, green when there
// is at least one, muted at zero.
function CountMetric({
  label,
  info,
  count,
}: {
  label: string;
  info: string;
  count: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="flex items-center gap-1 text-sm text-ds-slate-700">
        {label}
        <InfoPopover label={label} align="left">
          {info}
        </InfoPopover>
      </span>
      <span
        className={cn(
          "inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold",
          count > 0
            ? "bg-emerald-50 text-emerald-700"
            : "bg-ds-slate-100 text-ds-slate-500"
        )}
      >
        {count}
      </span>
    </div>
  );
}

/**
 * The bottom sheet a citation number opens: the cited passage(s) this source
 * backed, the source's bibliographic header (title + journal + tier), and the
 * deterministic scores behind it (each with an info tooltip). A link opens the
 * article (internal) / the work details (web work) on the CURRENT host, or the
 * paper's DOI (external) when there is no Juisci page.
 */
/** Whether the study card shows, is still being decided, or will not come. */
type StudyCardState =
  | { state: "pending" }
  | { state: "absent" }
  // The reading and the language it is stated in travel together: the
  // appraisal follows the ARTICLE it came from, not the reader's app
  // (docs/data-ingestion/risk-of-bias.md), and a state holding one without the
  // other could put a French reading under English questions.
  | { state: "shown"; level: ShownAppraisal; language: ReadingLanguage };

export function EvidenceCitationSheet({
  source,
  citationNumber,
  quotes,
  roomId,
  isOpen,
  onClose,
}: {
  source: EvidenceSourceView | null;
  citationNumber: number | null;
  // The verbatim passage(s) this source was cited for (gate-verified quotes).
  quotes: readonly string[];
  // The room this answer is read in, `null` in the public app. The api
  // withholds the appraisal inside a client's room.
  roomId: string | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Room-agnostic on purpose: the link baked into an answer is the global
  // article path too, so both routes into the paper agree.
  const { articleLink } = usePageLinks(null);
  // A Juisci page for this source (article for internal, work details for a web
  // work) — resolved to a same-host path. Null when the source has no Juisci
  // page, in which case the DOI link (external) is offered instead.
  const juisciPath =
    source !== null && source.juisciLink !== null
      ? samehostPath(source.juisciLink)
      : null;

  // Methodological identity for THIS source's Work, read live (not from the
  // frozen answer snapshot, so a later re-analysis shows). Gated behind the
  // experimental flag + a resolved Work (uncrawled web sources have none).
  //
  // This is the THIN readout: the design and which grid applies. The study
  // card below states the same design plus what was assessed and the verdict,
  // so the two are mutually exclusive — see `showsStudyCard`.
  const methodEnabled = useFeatureIsOn("METHOD_READOUT");
  const workId = source !== null ? source.workId : null;
  const methodQuery = useApiQuery(
    "/webapp/private/works/risk-of-bias",
    methodEnabled && workId !== null ? { query: { ids: workId } } : false
  );
  const methodRow =
    methodQuery.state === "success"
      ? methodQuery.body.find((row) => row.workId === workId)
      : undefined;
  const method = methodRow === undefined ? null : methodRow.method;

  // The works score behind this source, when the source IS a Juisci Work. The
  // frozen answer carries neither the journal rank nor the evidence level (and
  // must not: a later re-appraisal has to show), so both are read live, the
  // way the method readout above is. Two hops, because the answer knows the
  // Work and the score hangs off the published article: the Work resolver
  // hands back that article's slug.
  const workResolution = useApiQuery(
    "/webapp/public/works/:id",
    workId === null ? false : { param: { id: workId }, durable: true }
  );
  const workSlug =
    workResolution.state === "success" && workResolution.body.kind === "article"
      ? workResolution.body.slug
      : null;
  const scoredArticle = useApiQuery(
    "/webapp/public/articles/:slug",
    workSlug === null
      ? false
      : {
          param: { slug: workSlug },
          query: articleDetailQuery(roomId),
          durable: true,
        }
  );
  const article = scoredArticle.state === "success" ? scoredArticle.body : null;
  // SIGN-OFF (Menie): the Evidence surface states the appraisal in full — the
  // study design, what was assessed, and the conduct verdict with its domains.
  //
  // It already carried the verdict's EFFECT: `JuisciGrade` is derived from
  // `EvidenceLevel`, which is the design anchor moved by this same risk-of-bias
  // reading (`domain/evidence-grade.ts`, and the parity test beside it). Naming
  // the design while hiding the verdict made the letter less explicable, not
  // more careful. The UI now matches the score it shows.
  //
  // Same two gates as every other surface: the flag, and the public app only
  // for now. The api withholds an appraisal with no usable target or one read
  // from an abstract, so nothing here has to check for those.
  const showLevel = useFeatureIsOn("WEBAPP_RISK_OF_BIAS");
  // A tri-state, not a nullable level: the card is decided by a TWO-HOP read
  // (work id → slug → article), and a plain null would read as "no card" while
  // that read is still in flight — the thin readout below would paint and then
  // be replaced by the card a moment later.
  const studyCard = ((): StudyCardState => {
    if (!showLevel || workId === null) return { state: "absent" };
    if (workResolution.state === "pending") return { state: "pending" };
    if (workSlug !== null && scoredArticle.state === "pending")
      return { state: "pending" };
    if (article === null || article.appraisal === null)
      return { state: "absent" };
    return {
      state: "shown",
      level: article.appraisal,
      language: article.language,
    };
  })();
  const journalName =
    source !== null && source.journal !== null
      ? source.journal
      : article !== null && article.journal !== null
        ? article.journal.name
        : null;

  // A source Juisci never published still names its journal, and that name is
  // enough to find the journal's rank — the same lookup the chat's source sheet
  // makes. Only asked when there is no article to read the rank from, so one
  // source never issues both.
  // Wait for the Work resolution to SETTLE before asking. `article` is still
  // null while the two reads are in flight, so a check on `article` alone asks
  // for every internal source too: one wasted request. A failed article read
  // also falls back here, rather than losing the journal.
  const noArticleComing =
    workId === null ||
    workResolution.state === "error" ||
    scoredArticle.state === "error" ||
    (workResolution.state === "success" &&
      workResolution.body.kind !== "article");
  const citedRank = useApiQuery(
    "/webapp/public/journals/rank",
    noArticleComing && journalName !== null
      ? { query: { name: journalName }, durable: true }
      : false
  );
  const rank =
    article !== null
      ? article.journalRank
      : citedRank.state === "success"
        ? citedRank.body
        : null;
  const standing: JournalStanding | null =
    rank === null
      ? null
      : {
          // The journal is already named under the title above.
          journalName: null,
          impactFactor: rank.impactFactor,
          // An unbanded journal still has an impact factor and its indexing.
          band: fieldBand(t, rank),
          indexing: indexedIn(rank),
          isCddRecommended: rank.cdd,
        };
  // A journal Juisci holds but has neither ranked nor indexed states nothing:
  // no block, and no divider above it.
  const journalStanding =
    standing !== null && journalStatesAnything(standing) ? standing : null;
  // The way to the paper inside Juisci. An answer written before the link was
  // stored carries none, so the Work resolution above stands in for it: the
  // paper IS in Juisci, and the button must not fall through to the DOI and
  // send the reader out of the app.
  const workPath =
    juisciPath !== null
      ? juisciPath
      : workSlug === null
        ? null
        : articleLink({ slug: workSlug });
  // The way out, shaped for the same mark the chat's source sheet uses: the
  // article in-app when the Work resolves, the DOI when it does not, and
  // nothing at all for a source with neither.
  // The DOI is the fallback for a paper Juisci does NOT hold, so it waits for
  // `noArticleComing` too. Offered while the Work resolver is still in flight,
  // it sends the reader off-site for a paper we were about to link in-app —
  // the very case this fallback exists to prevent. No mark for a beat is
  // better than the wrong destination.
  const target: SourceTarget | null =
    workPath !== null
      ? { kind: "internal", path: workPath }
      : noArticleComing && source !== null && source.doiLink !== null
        ? { kind: "external", url: source.doiLink }
        : null;
  // Same gap as the journal, same fix: a web-retrieved source carries no year,
  // and the article we resolved holds the publication date it is missing. A
  // clinician places a paper by author AND year, so a bare author reads as
  // half an identity.
  const identity =
    source === null
      ? ""
      : citationIdentity({
          authors: source.authors,
          year:
            source.year !== null
              ? source.year
              : article === null
                ? null
                : publicationYear(article.publication_date),
        });

  // The identity, as the chat's source sheet states one. Assembled from the
  // frozen source plus what the resolved article adds (its journal, its
  // publication type), then handed to the SAME component — so one paper reads
  // identically whichever mode cited it, and neither sheet can drift.
  const identitySource: ChatSource | null =
    source === null
      ? null
      : {
          key: source.id,
          container:
            journalName === null
              ? null
              : { kind: "journal", name: journalName },
          title: source.title === null ? t("Untitled source") : source.title,
          identity,
          design:
            article === null || article.article_type === null
              ? null
              : {
                  label: getArticleTypeText(article.article_type),
                  variant: getArticleTypeVariant(article.article_type),
                },
          resolution: "resolved",
          signals: { quality: source.quality, endorsement: source.endorsement },
          target,
        };

  return (
    <BottomPanel
      isOpen={isOpen}
      onClose={onClose}
      title={
        citationNumber === null
          ? t("Source")
          : t("Source {{n}}", { n: citationNumber })
      }
      displayCloseButton
    >
      {source === null || identitySource === null ? (
        <p className="px-5 py-6 text-footnote text-ds-slate-600">
          {t("This source is no longer available.")}
        </p>
      ) : (
        <div className="flex flex-col gap-3 px-5 pt-5 pb-6 font-dm-sans">
          <div className="flex items-end gap-2">
            <ChatSourceIdentity source={identitySource} size="sheet" />
            {target !== null && <SourceLink target={target} />}
          </div>

          {/* The works score, the same two halves every other surface shows. */}
          {journalStanding !== null && (
            <>
              <hr className="m-0 border-t border-ds-slate-100" />
              <JournalStandingSection {...journalStanding} />
            </>
          )}

          {studyCard.state === "shown" && (
            <>
              <hr className="m-0 border-t border-ds-slate-100" />
              <StudyAppraisalLedger
                level={studyCard.level}
                language={studyCard.language}
              />
            </>
          )}

          {/* From here down is what only an Evidence turn knows: how this
              answer reached the paper, and the passages it was cited for. */}
          <hr className="m-0 border-t border-ds-slate-100" />
          <div className="flex flex-col divide-y divide-ds-slate-100 rounded-xl bg-ds-slate-50 px-3 py-1">
            <SignalMetric
              label={t("Source quality")}
              value={source.quality}
              info={<SourceQualityExplanation />}
            />
            <SignalMetric
              label={t("Endorsement")}
              value={source.endorsement}
              info={t("How widely the field has cited this work.")}
            />
            <SignalMetric
              label={t("Retrieval match")}
              value={source.retrievalConfidence}
              info={t(
                "How closely this source matched your question during retrieval."
              )}
            />
            <CountMetric
              label={t("High-tier citers")}
              count={source.inboundHighTierCiters}
              info={t(
                "How many guidelines or evidence syntheses cite this source."
              )}
            />
          </div>

          {/* Show the method block only for a source that HAS a settled parse-time
              appraisal (appraised or "no grid applies"). While coverage is thin we
              hide the "Not yet analysed." state entirely (`method === null`) rather
              than surface an empty readout. */}
          {/* Only once the card is decided AGAINST: showing it while the
              two-hop read is pending paints the thin readout and then swaps
              the card in under the reader. */}
          {studyCard.state === "absent" &&
            methodEnabled &&
            workId !== null &&
            methodQuery.state === "success" &&
            method !== null && (
              <div className="rounded-xl bg-ds-slate-50 px-3 py-2">
                <SourceMethodReadout method={method} />
              </div>
            )}

          {quotes.length > 0 && <CitedPassages quotes={quotes} />}
        </div>
      )}
    </BottomPanel>
  );
}
